import {isPhysicalPrintJob, isPrefetchPrintJob} from '../mappers/fromPrintJobs'
import {PrintStoryDoc} from '../types'

export type PrintSourceKind = 'auto' | 'staff' | 'scan' | 'other'

export type ConfirmedBySource = {
  auto: number
  staff: number
  scan: number
  other: number
}

export type LastRequestSnapshot = {
  requestedCopies: number
  confirmedCopies: number
  jobId?: string
}

export type PrefetchDeviceStats = {
  uniqueTargetDevices: number
  confirmedDevices: number
  deliveryAttempts: number
}

export type ContentPrintSummary = {
  autoRequestedCopies: number
  confirmedCopiesTotal: number
  physicalPrintJobs: number
  printAttempts: number
  confirmedBySource: ConfirmedBySource
  lastRequest: LastRequestSnapshot
  prefetch: PrefetchDeviceStats
}

const COMPLETED_STATUSES = new Set(['completed', 'complete', 'success'])
const NON_CONFIRMING_STATUSES = new Set([
  'blocked',
  'failed',
  'error',
  'errored',
  'callback_missing',
])

export const getFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }

  return undefined
}

export const getJobStatus = (job: PrintStoryDoc): string => String(job?.status || '').toLowerCase()

export const isCompletedPhysicalStatus = (status: string): boolean => COMPLETED_STATUSES.has(status)

export const isNonConfirmingStatus = (status: string): boolean => NON_CONFIRMING_STATUSES.has(status)

export const getAutoRequestedCopies = (content?: PrintStoryDoc | null): number => {
  return (
    getFiniteNumber(content?.autoPrintRequestedCopies) ??
    getFiniteNumber(content?.autoPrintExpectedCopies) ??
    getFiniteNumber(content?.autoPrintCopies) ??
    0
  )
}

export const getLastRequestSnapshot = (content?: PrintStoryDoc | null): LastRequestSnapshot => {
  const jobId = content?.lastPrintJob ? String(content.lastPrintJob) : undefined
  return {
    requestedCopies: getFiniteNumber(content?.lastPrintRequestedCopies) ?? 0,
    confirmedCopies: getFiniteNumber(content?.lastPrintConfirmedCopies) ?? 0,
    jobId,
  }
}

/** Explicit confirmed copies only. Never assume 1. */
export const getTrustedConfirmedCopies = (job: PrintStoryDoc): number | undefined => {
  return getFiniteNumber(job?.confirmedCopies) ?? getFiniteNumber(job?.mqttCallback?.confirmedCopies)
}

export const getAutoPrintJobIds = (content?: PrintStoryDoc | null): Set<string> => {
  const ids = new Set<string>()
  const candidates = [
    content?.autoPrintPrintJobId,
    content?.autoPrintParentPrintJobId,
    content?.autoPrintLastCallbackJobId,
  ]

  for (const value of candidates) {
    if (value) {
      ids.add(String(value))
    }
  }

  const childIds = content?.autoPrintChildPrintJobIds
  const children = Array.isArray(childIds) ? childIds : []
  for (const value of children) {
    if (value) {
      ids.add(String(value))
    }
  }

  return ids
}

export const normalizePrintSource = (raw: unknown): PrintSourceKind => {
  const source = String(raw || '').toLowerCase().trim()
  if (!source) {
    return 'other'
  }

  if (
    source.startsWith('auto_print') ||
    source.startsWith('auto-print') ||
    source.includes('auto_print') ||
    source.includes('auto-print') ||
    (source.includes('auto') && source.includes('print')) ||
    source.startsWith('auto_')
  ) {
    return 'auto'
  }

  if (source === 'api_staff_print' || source.includes('staff') || source.includes('manual')) {
    return 'staff'
  }

  if (source.startsWith('api_scan_print') || source.includes('scan') || source.includes('qr')) {
    return 'scan'
  }

  return 'other'
}

export const classifyPrintSource = (
  job: PrintStoryDoc,
  autoJobIds: Set<string>
): PrintSourceKind => {
  const jobId = String(job?.jobId || '')
  const docId = String(job?.id || '')
  if ((jobId && autoJobIds.has(jobId)) || (docId && autoJobIds.has(docId))) {
    return 'auto'
  }

  return normalizePrintSource(job?.source || job?.mqttPayload?.source)
}

export const getPhysicalPrintJobs = (printJobs: PrintStoryDoc[] = []): PrintStoryDoc[] =>
  printJobs.filter((job) => isPhysicalPrintJob(job) && !isPrefetchPrintJob(job))

export const getJobIdentity = (job: PrintStoryDoc): string => String(job?.jobId || job?.id || '')

/**
 * If completed children and their parent both exist, keep children only so
 * parent+child records cannot double-count copies or successful jobs.
 */
export const excludeSplitCopyParents = (jobs: PrintStoryDoc[]): PrintStoryDoc[] => {
  const identities = new Set(jobs.map(getJobIdentity).filter(Boolean))
  const parentIdsWithChildren = new Set(
    jobs
      .map((job) => String(job?.parentPrintJobId || ''))
      .filter((parentId) => parentId && identities.has(parentId))
  )

  if (parentIdsWithChildren.size === 0) {
    return jobs
  }

  return jobs.filter((job) => !parentIdsWithChildren.has(getJobIdentity(job)))
}

export const getCompletedConfirmingJobs = (physicalJobs: PrintStoryDoc[]): PrintStoryDoc[] => {
  const completed = physicalJobs.filter((job) => {
    const status = getJobStatus(job)
    if (isNonConfirmingStatus(status) || !isCompletedPhysicalStatus(status)) {
      return false
    }
    return getTrustedConfirmedCopies(job) !== undefined
  })

  return excludeSplitCopyParents(completed)
}

export const getPrefetchDeviceStats = (printJobs: PrintStoryDoc[] = []): PrefetchDeviceStats => {
  const prefetchJobs = printJobs.filter(isPrefetchPrintJob)
  const uniqueTargetDevices = Array.from(
    new Set(prefetchJobs.map((job) => String(job.deviceId || '')).filter(Boolean))
  )
  const confirmedDevices = uniqueTargetDevices.filter((deviceId) =>
    prefetchJobs.some((job) => String(job.deviceId || '') === deviceId && isPrefetchJobConfirmed(job))
  )

  return {
    uniqueTargetDevices: uniqueTargetDevices.length,
    confirmedDevices: confirmedDevices.length,
    deliveryAttempts: prefetchJobs.length,
  }
}

const isPrefetchJobConfirmed = (job: PrintStoryDoc): boolean => {
  const status = getJobStatus(job)
  if (['completed', 'complete', 'success', 'succeeded'].includes(status)) {
    return true
  }
  const result = String(job.mqttCallback?.result || '').toLowerCase()
  const stage = String(job.mqttCallback?.stage || '').toLowerCase()
  return result === 'success' && stage === 'prefetch'
}

export const formatConfirmedBySource = (bySource: ConfirmedBySource): string => {
  const parts: string[] = []
  if (bySource.auto > 0) {
    parts.push(`Auto ${bySource.auto}`)
  }
  if (bySource.staff > 0) {
    parts.push(`Staff ${bySource.staff}`)
  }
  if (bySource.scan > 0) {
    parts.push(`Scan ${bySource.scan}`)
  }
  if (bySource.other > 0) {
    parts.push(`Other ${bySource.other}`)
  }
  return parts.join(' · ')
}

export const buildContentPrintSummary = ({
  content,
  printJobs = [],
}: {
  content?: PrintStoryDoc | null
  printJobs?: PrintStoryDoc[]
}): ContentPrintSummary => {
  const physicalJobs = getPhysicalPrintJobs(printJobs)
  const confirmingJobs = getCompletedConfirmingJobs(physicalJobs)
  const autoJobIds = getAutoPrintJobIds(content)
  const confirmedBySource: ConfirmedBySource = {auto: 0, staff: 0, scan: 0, other: 0}

  let confirmedCopiesTotal = 0
  for (const job of confirmingJobs) {
    const copies = getTrustedConfirmedCopies(job) ?? 0
    confirmedCopiesTotal += copies
    confirmedBySource[classifyPrintSource(job, autoJobIds)] += copies
  }

  return {
    autoRequestedCopies: getAutoRequestedCopies(content),
    confirmedCopiesTotal,
    physicalPrintJobs: confirmingJobs.length,
    printAttempts: physicalJobs.length,
    confirmedBySource,
    lastRequest: getLastRequestSnapshot(content),
    prefetch: getPrefetchDeviceStats(printJobs),
  }
}
